import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Languages, Check, Loader2 } from 'lucide-react';
import { LANGUAGES } from '../mock';
import { useI18n } from '../context/I18nContext';

export default function LanguageSelector() {
  const { lang, setLang, t, loading } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [pendingLang, setPendingLang] = React.useState(null);

  const handleSelect = (code) => {
    setLang(code);
    setPendingLang(code);
  };

  // Clear pending indicator when the fetch finishes for the selected lang
  React.useEffect(() => {
    if (!loading && pendingLang === lang) {
      // Small delay so the check mark is clearly visible before closing
      const id = setTimeout(() => {
        setPendingLang(null);
        setOpen(false);
      }, 200);
      return () => clearTimeout(id);
    }
  }, [loading, pendingLang, lang]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Select language"
          data-testid="language-selector-trigger"
          className="p-2 rounded-md hover:bg-gray-100 transition-colors relative"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 text-[#f5a623] animate-spin" strokeWidth={2.5} />
          ) : (
            <Languages className="w-5 h-5 text-gray-700" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={10}
        className="w-[520px] max-w-[92vw] p-0 rounded-xl border border-gray-200 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.15)] bg-white"
      >
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-[15px] font-medium text-gray-800">{t.header.select_language}</div>
          {loading && (
            <div className="flex items-center gap-1.5 text-xs text-[#f5a623] font-semibold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} />
              <span>Translating\u2026</span>
            </div>
          )}
        </div>
        <div className="px-5 py-5">
          <div className="grid grid-cols-3 gap-x-5 gap-y-4">
            {LANGUAGES.map((item) => {
              const isSelected = lang === item.code;
              const isPending = loading && pendingLang === item.code;
              return (
                <button
                  key={item.code}
                  onClick={() => handleSelect(item.code)}
                  disabled={loading}
                  data-testid={`language-option-${item.code}`}
                  className={`flex items-center gap-2.5 text-left group py-1.5 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                >
                  <span className="text-base leading-none w-6 h-6 flex items-center justify-center rounded overflow-hidden bg-gray-50 border border-gray-100 shrink-0">
                    {item.flag}
                  </span>
                  <span className="text-[14px] text-gray-800 group-hover:text-[#f5a623] transition-colors truncate">
                    {item.name.length > 14 ? item.name.slice(0, 12) + '\u2026' : item.name}
                  </span>
                  {isPending ? (
                    <Loader2 className="w-4 h-4 text-[#f5a623] ml-auto shrink-0 animate-spin" strokeWidth={3} />
                  ) : isSelected ? (
                    <Check className="w-4 h-4 text-[#3b82f6] ml-auto shrink-0" strokeWidth={3} />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
